import { orderApi, } from '@sellsen/generated-sources';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
const QUERY_KEYS = {
    allOrders: ['orders'],
    list: (params) => [...QUERY_KEYS.allOrders, 'list', params],
    tracking: (params) => [...QUERY_KEYS.allOrders, 'tracking', params],
    trackingCounts: (params) => [...QUERY_KEYS.allOrders, 'tracking-counts', params],
    detail: (params) => [...QUERY_KEYS.allOrders, 'detail', params],
    search: (params) => [...QUERY_KEYS.allOrders, 'search', params],
};
/** Üst çubuk araması için sunucuyla aynı alt sınır; daha kısa terimde istek atılmaz. */
export const ORDER_SEARCH_MIN_LENGTH = 3;
/**
 * Pazarlar arası hızlı sipariş araması (üst çubuk). Seçili mağazadan bağımsızdır; sonuç sade
 * satırlardır, seçim mağazayı değiştirip detaya gider.
 */
export const useSearchOrdersAcrossMarketplacesQuery = (params, enabled = true) => {
    return useQuery({
        queryKey: QUERY_KEYS.search(params),
        queryFn: () => orderApi.searchOrdersAcrossMarketplaces(params),
        enabled: enabled && params.q.trim().length >= ORDER_SEARCH_MIN_LENGTH,
        staleTime: 30_000,
        placeholderData: (previous) => previous,
    });
};
/** Liste filtresine uyan siparişlerin Excel satırları. Sayfa boyutu sunucuda yok sayılır. */
export const useExportOrdersMutation = () => useMutation({
    mutationFn: (params) => orderApi.exportOrders(params),
});
/** Takip ekranı filtresine uyan siparişlerin Excel satırları. */
export const useExportOrderTrackingMutation = () => useMutation({
    mutationFn: (params) => orderApi.exportOrderTracking(params),
});
export const useListOrdersQuery = (params, enabled = true) => {
    return useQuery({
        queryKey: QUERY_KEYS.list(params),
        queryFn: () => orderApi.listOrders(params),
        enabled: enabled && params.marketplace != null,
    });
};
export const useListOrderTrackingQuery = (params, enabled = true) => {
    return useQuery({
        queryKey: QUERY_KEYS.tracking(params),
        queryFn: () => orderApi.listOrderTracking(params),
        enabled,
    });
};
/**
 * Takip ekranı hızlı filtre sayaçları. Gövde liste isteğiyle aynıdır; sunucu hızlı filtre
 * alanlarını yok sayıp her sayacı ayrı hesaplar. `allOrders` altında olduğu için sipariş
 * mutasyonları sayaçları da tazeler.
 */
export const useOrderTrackingCountsQuery = (params, enabled = true) => {
    return useQuery({
        queryKey: QUERY_KEYS.trackingCounts(params),
        queryFn: () => orderApi.countOrderTrackingQuickFilters(params),
        enabled,
        staleTime: 30_000,
        placeholderData: (previous) => previous,
    });
};
export const useGetOrderQuery = (params, enabled = true) => {
    return useQuery({
        queryKey: QUERY_KEYS.detail(params),
        queryFn: () => orderApi.getOrder(params),
        enabled: enabled &&
            params.marketplace != null &&
            Boolean(params.amazonOrderId?.trim()),
    });
};
/**
 * Karlılık override'ları. Sunucu gönderilen seti komple replace eder: alan yoksa/boşsa o kalemin
 * override'ı silinir ve otomatik hesaba dönülür. Yanıt yeniden hesaplanmış paneli döndürür, ama
 * yalnız ona güvenip cache'i elle yazmıyoruz: sunucu bağımlı kalemleri (vergi, komisyon) de
 * yeniden türettiği için detay sorgusunu tazelemek en güvenlisi.
 */
export const useUpdateOrderProfitabilityMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (params) => orderApi.updateProfitability(params),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.allOrders });
        },
    });
};
export const useUpdateOrderNoteMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (params) => orderApi.updateOrderNote(params),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.allOrders });
        },
    });
};
/** Siparişe bayrak atar / kaldırır (flagId null). Liste ve detay aynı alanı gösterir. */
export const useAssignOrderFlagMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (params) => orderApi.assignOrderFlag(params),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.allOrders });
        },
    });
};
export const useUpdateBuyerOrderIdMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (params) => orderApi.updateBuyerOrderId(params),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.allOrders });
        },
    });
};
/**
 * Takip no doğrulamasını hemen çalıştırır ("Şimdi kontrol et"): backend Amazon'daki
 * (Seller Central) ve ShippingLot'taki numaraları okuyup kıyası pakete yazar. Paket başına
 * 60 sn'den sık istek 429 döner. Sonuç detay ve takip listesinde göründüğü için tüm sipariş
 * sorguları tazelenir.
 */
export const useCheckOrderTrackingMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (params) => orderApi.checkOrderTracking(params),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.allOrders });
        },
    });
};
/**
 * Sipariş durumu (orderStatus) yalnız SP-API senkronizasyonuyla değişir; manuel güncelleme ucu
 * yoktur. Satın alma / depo akışı için `useUpdatePackageStatusMutation` kullanılır.
 */
